import { ReactNode, forwardRef, useState, isValidElement, ReactElement } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { Box, SxProps, Tooltip as MuiTooltip, Typography } from '@mui/material';
import { overflowTooltipStyle, overflowingContentStyle } from './Tooltip.styles';

interface OverflowTooltipProps {
  backgroundColor?: string;
  children: ReactElement | string;
  tooltipTextColor?: string;
  fontColor?: string;
  sx?: SxProps;
  tooltipArrow?: boolean;
  /** Show the tooltip on hover regardless of whether the content overflows. Use when tooltipText conveys information beyond the rendered content. */
  forceShow?: boolean;
  tooltipPlacement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end';
  tooltipText?: string | ReactNode;
  /** Number of lines to wrap onto before truncating with an ellipsis. Default 1 (single-line, width-based overflow). */
  lines?: number;
}

export const OverflowTooltip = forwardRef<HTMLDivElement, OverflowTooltipProps>((props, ref) => {
  const {
    backgroundColor: tooltipBgColor = 'primary.dark',
    children,
    tooltipTextColor = 'common.white',
    fontColor,
    sx,
    tooltipArrow = true,
    tooltipPlacement = 'top',
    tooltipText,
    forceShow = false,
    lines = 1,
    ...other
  } = props;

  const [showTooltip, setShowTooltip] = useState(false);

  const { ref: resizeRef } = useResizeDetector({
    onResize: () => {
      if (resizeRef.current) {
        const { scrollWidth, clientWidth, scrollHeight, clientHeight } = resizeRef.current;
        setShowTooltip(lines > 1 ? scrollHeight > clientHeight : scrollWidth > clientWidth);
      }
    },
  });

  const getRenderValue = (item?: string | ReactNode) => {
    if (typeof item === 'string') {
      return (
        <Typography
          variant='bodySmall'
          sx={{
            color: tooltipTextColor,
          }}
        >
          {item}
        </Typography>
      );
    }

    if (item && isValidElement(item)) {
      return item;
    }

    return null;
  };
  return (
    <MuiTooltip
      arrow={tooltipArrow}
      disableFocusListener={!forceShow && !showTooltip}
      disableHoverListener={!forceShow && !showTooltip}
      disableTouchListener={!forceShow && !showTooltip}
      enterDelay={500}
      placement={tooltipPlacement}
      ref={ref}
      title={tooltipText ? getRenderValue(tooltipText) : getRenderValue(children)}
      slotProps={{
        tooltip: {
          sx: {
            ...overflowTooltipStyle,
            backgroundColor: tooltipBgColor,
            '.MuiTooltip-arrow': {
              '&:before': {
                backgroundColor: tooltipBgColor,
              },
            },
            ...(sx as SxProps),
          },
        },
      }}
      {...other}
    >
      <Box
        ref={resizeRef}
        sx={{
          ...(lines > 1
            ? {
                display: '-webkit-box',
                WebkitLineClamp: lines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'normal',
              }
            : overflowingContentStyle),
          color: fontColor,
        }}
      >
        {children}
      </Box>
    </MuiTooltip>
  );
});

OverflowTooltip.displayName = 'OverflowTooltip';
